/**
 * invoicePdf.js — build a real PDF quote/invoice in the browser.
 *
 * Ported from v10's buildInvoicePdf, adapted to snake_case columns. Kept
 * dependency-free on purpose: it writes the PDF bytes directly, so there is no
 * PDF library to pull in, and the output is identical to what the old app
 * produced.
 *
 * It renders values, it does not compute them — every figure comes from the
 * rental record, priced by the maths in rental-time.js which is pinned by
 * scripts/parity.test.mjs.
 */

import { fmtMoney, fmtDate, todayStr } from "../lib/format.js";
import { fmtTime, rentalDays, lateInfo } from "../lib/rental-time.js";
import { QUOTE_TEXT } from "./Invoice.jsx";

/**
 * Base-14 PDF fonts are WinAnsi; anything outside ASCII would need an embedded
 * font. Substituting the few characters we actually use keeps the file tiny.
 */
function pdfClean(t) {
  return String(t == null ? "" : t)
    .replace(/—|–/g, "-")
    .replace(/·/g, ".")
    .replace(/→/g, "to")
    .replace(/⚠/g, "")
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/[^\x20-\x7E]/g, "");
}

const pdfEsc = (t) => t.replace(/\\/g, "\\\\").replace(/\(/g, "\\(").replace(/\)/g, "\\)");

/** Rough Helvetica advance width. Good enough for wrapping and right-alignment. */
const pdfW = (t, sz, bold) => t.length * sz * (bold ? 0.56 : 0.5);

function pdfWrap(t, sz, maxW, bold) {
  const words = pdfClean(t).split(/\s+/);
  const lines = [];
  let cur = "";
  for (const w of words) {
    const test = cur ? `${cur} ${w}` : w;
    if (pdfW(test, sz, bold) > maxW && cur) {
      lines.push(cur);
      cur = w;
    } else {
      cur = test;
    }
  }
  if (cur) lines.push(cur);
  return lines;
}

export function buildInvoicePdf(rental, settings) {
  const eq = rental.equipment ?? {};
  const c = rental.customers ?? {};
  const s = settings ?? {};
  const invNo = rental.invoice_no ?? "";
  const days = rentalDays(rental);
  const charge = rental.rental_charge ?? 0;
  const pending = rental.status === "reserved";
  const isFinal = rental.status === "returned";
  const docType = isFinal ? "INVOICE" : "QUOTE";
  const li = lateInfo(rental);

  const INK = "0.11 0.12 0.14";
  const GRAY = "0.42 0.44 0.46";
  const ORANGE = "0.91 0.39 0.11";
  const RED = "0.70 0.23 0.17";
  const GREEN = "0.18 0.49 0.27";
  const WHITE = "1 1 1";

  const L = 54;
  const Rx = 558;
  const ops = [];

  const T = (x, y, txt, o = {}) => {
    const t = pdfClean(txt);
    if (!t) return;
    const f = o.bold ? "F2" : "F1";
    const sz = o.sz || 10;
    const col = o.col || INK;
    const xx = o.align === "right" ? x - pdfW(t, sz, o.bold) : x;
    ops.push(`BT /${f} ${sz} Tf ${col} rg 1 0 0 1 ${xx.toFixed(1)} ${y.toFixed(1)} Tm (${pdfEsc(t)}) Tj ET`);
  };
  const rect = (x, y, w, h, col) => ops.push(`${col} rg ${x} ${y} ${w} ${h} re f`);
  const box = (x, y, w, h, col, lw) => ops.push(`${col} RG ${lw || 1} w ${x} ${y} ${w} ${h} re S`);
  const hline = (y, col, lw) => ops.push(`${col} RG ${lw || 1} w ${L} ${y} m ${Rx} ${y} l S`);

  let y = 758;

  T(L, y, "HAL", { bold: true, sz: 26 });
  T(L + 55, y, "CO", { bold: true, sz: 26, col: ORANGE });
  T(Rx, y, docType, { bold: true, sz: 22, align: "right" });

  y -= 17;
  T(L, y, s.company, { bold: true, sz: 11 });
  T(Rx, y, invNo || "(draft)", { bold: true, sz: 11, align: "right" });

  y -= 13;
  T(L, y, `${s.addr} . ${s.phone}`, { col: GRAY, sz: 9.5 });
  T(Rx, y, `Issued ${fmtDate(todayStr())}`, { col: GRAY, sz: 9.5, align: "right" });

  y -= 12;
  T(L, y, s.email, { col: GRAY, sz: 9.5 });

  const stampTxt = rental.paid ? "PAID" : isFinal ? "BALANCE DUE" : pending ? "RESERVATION HELD" : "ESTIMATE";
  const stampCol = rental.paid ? GREEN : (isFinal || pending) ? RED : "0.23 0.26 0.29";
  const stw = pdfW(stampTxt, 13, true) + 16;
  box(Rx - stw, y - 6, stw, 22, stampCol, 1.6);
  T(Rx - stw + 8, y, stampTxt, { bold: true, sz: 13, col: stampCol });

  y -= 22;
  hline(y, INK, 2.5);
  y -= 20;

  // Only the quote disclaimer. v10 also printed a "This is an invoice" banner
  // on reserved rentals, directly under a heading reading QUOTE; removed.
  if (!isFinal) {
    const noteLines = pdfWrap(QUOTE_TEXT, 9.5, Rx - L - 24, false);
    const nh = noteLines.length * 12 + 14;
    rect(L, y - nh + 12, Rx - L, nh, "0.94 0.94 0.92");
    box(L, y - nh + 12, Rx - L, nh, "0.35 0.38 0.41", 1.4);
    let ny = y - 2;
    noteLines.forEach((ln) => {
      T(L + 12, ny, ln, { sz: 9.5, col: "0.23 0.26 0.29", bold: true });
      ny -= 12;
    });
    y = y - nh + 2;
    y -= 14;
  }

  const colY = y;
  T(L, y, "BILL TO", { bold: true, sz: 9, col: GRAY });
  let by = y - 14;
  [c.name, c.business, c.billing_address || c.home_address, c.phone, c.email]
    .filter(Boolean)
    .forEach((v, i) => {
      T(L, by, v, { sz: 10, bold: i === 0 });
      by -= 13;
    });

  T(320, colY, "RENTAL PERIOD", { bold: true, sz: 9, col: GRAY });
  let py = colY - 14;
  T(320, py, `${fmtDate(rental.start_date)} ${fmtTime(rental.start_time)} to`, { sz: 10 });
  py -= 13;
  T(320, py, `${fmtDate(rental.end_date)} ${fmtTime(rental.end_time)} (${days} day${days === 1 ? "" : "s"})`, { sz: 10 });
  py -= 13;
  if (isFinal && rental.returned_date) {
    T(320, py, `Returned ${fmtDate(rental.returned_date)} ${fmtTime(rental.returned_time)}`, { sz: 10 });
    py -= 13;
    if (li) {
      T(320, py, `${li.label} LATE`, { sz: 10, bold: true, col: RED });
      py -= 13;
    }
  }
  if (Number(rental.deposit) > 0) {
    T(320, py, `Security deposit held: ${fmtMoney(rental.deposit)}`, { sz: 10 });
    py -= 13;
  }

  y = Math.min(by, py) - 10;

  rect(L, y - 5, Rx - L, 20, INK);
  T(L + 10, y, "DESCRIPTION", { bold: true, sz: 9.5, col: WHITE });
  T(Rx - 10, y, "AMOUNT", { bold: true, sz: 9.5, col: WHITE, align: "right" });
  y -= 24;

  const row = (label, sub, amt) => {
    T(L + 10, y, label, { sz: 10.5 });
    T(Rx - 10, y, amt, { sz: 10.5, align: "right" });
    if (sub) {
      y -= 12;
      T(L + 10, y, sub, { sz: 8.5, col: GRAY });
    }
    y -= 10;
    hline(y, "0.86 0.85 0.82", 0.7);
    y -= 15;
  };

  row(
    `Equipment rental - ${eq.name || "Equipment"}`,
    `${days} day${days === 1 ? "" : "s"} . rates ${fmtMoney(eq.day_rate)}/day, ${fmtMoney(eq.week_rate)}/wk, ${fmtMoney(eq.month_rate)}/mo`,
    fmtMoney(charge),
  );
  if (Number(rental.delivery_fee) > 0) row("Delivery & pickup", null, fmtMoney(rental.delivery_fee));
  if (Number(rental.damage_waiver) > 0) row("Damage waiver", null, fmtMoney(rental.damage_waiver));
  if (Number(rental.late_charge) > 0) row("Late return fee", li ? `${li.label} past due-back time` : null, fmtMoney(rental.late_charge));
  if (Number(rental.fuel_charge) > 0) row("Fuel charge", null, fmtMoney(rental.fuel_charge));
  if (Number(rental.damage_charge) > 0) row("Damage charge", null, fmtMoney(rental.damage_charge));
  if (Number(rental.other_fee) > 0) row(rental.other_fee_note || "Additional fee", null, fmtMoney(rental.other_fee));

  T(Rx - 120, y, `${isFinal ? "Total " : "Est. total "}${rental.paid ? "paid" : "due"}`, { bold: true, sz: 12, align: "right" });
  T(Rx - 10, y, fmtMoney(rental.total), { bold: true, sz: 12, align: "right" });
  y -= 24;

  if (rental.notes) {
    T(L, y, "NOTES", { bold: true, sz: 9, col: GRAY });
    y -= 13;
    pdfWrap(rental.notes, 9.5, Rx - L, false).slice(0, 6).forEach((ln) => {
      T(L, y, ln, { sz: 9.5 });
      y -= 12;
    });
    y -= 8;
  }

  hline(y, "0.86 0.85 0.82", 0.9);
  y -= 15;
  pdfWrap(
    `Thank you for renting with ${s.company}. ${s.delivery_note ?? ""}. Questions? Call ${s.phone}.`,
    9, Rx - L, false,
  ).forEach((ln) => {
    T(L, y, ln, { sz: 9, col: GRAY });
    y -= 11;
  });

  const stream = ops.join("\n");
  const objs = [
    "<</Type/Catalog/Pages 2 0 R>>",
    "<</Type/Pages/Kids[3 0 R]/Count 1>>",
    "<</Type/Page/Parent 2 0 R/MediaBox[0 0 612 792]/Resources<</Font<</F1 5 0 R/F2 6 0 R>>>>/Contents 4 0 R>>",
    `<</Length ${stream.length}>>stream\n${stream}\nendstream`,
    "<</Type/Font/Subtype/Type1/BaseFont/Helvetica>>",
    "<</Type/Font/Subtype/Type1/BaseFont/Helvetica-Bold>>",
  ];

  let pdf = "%PDF-1.4\n";
  const offsets = [];
  objs.forEach((o, i) => {
    offsets.push(pdf.length);
    pdf += `${i + 1} 0 obj${o}\nendobj\n`;
  });
  const xrefPos = pdf.length;
  pdf += `xref\n0 ${objs.length + 1}\n0000000000 65535 f \n`;
  offsets.forEach((o) => { pdf += `${String(o).padStart(10, "0")} 00000 n \n`; });
  pdf += `trailer<</Size ${objs.length + 1}/Root 1 0 R>>\nstartxref\n${xrefPos}\n%%EOF`;

  return pdf;
}

/** Base64 of the PDF, for emailing as an attachment. */
export function buildInvoicePdfBase64(rental, settings) {
  const pdf = buildInvoicePdf(rental, settings);
  // The generator emits only ASCII (pdfClean strips the rest), so charCode
  // maps 1:1 to bytes and btoa is safe.
  return btoa(pdf);
}

export function invoicePdfFilename(rental) {
  const kind = rental.status === "returned" ? "invoice" : "quote";
  return `HALCO-${kind}-${rental.invoice_no || "draft"}.pdf`;
}

export function downloadInvoicePdf(rental, settings) {
  const blob = new Blob([buildInvoicePdf(rental, settings)], { type: "application/pdf" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = invoicePdfFilename(rental);
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}
